import { IndividualsDuplexerChannel } from '@wix/ai-scheduling-assistant-common-lib/commonTypes';

import { bumpConversationTo } from './conversations';
import { scheduleChatsInvalidation } from './chats';
import { useDuplexerSubscription } from './use-duplexer-subscription';

interface ConversationPush {
  id?: string;
  updatedDate?: string;
}

/**
 * Keep the sidebar's order live off the CONVERSATION duplexer channel: re-stamp the row the frame is
 * about so it rises to the top immediately, with no refetch.
 *
 * The sidebar's other realtime subscription (`useDuplexer` in AppFrame) listens to ASSISTANT_CHAT, which
 * only fires once a turn has produced an assistant row. A message sent from the user's PHONE therefore
 * did not reorder this tab until the assistant answered — or until the tab was re-focused. The
 * CONVERSATION channel is the frame that says "this conversation just had activity, at this time",
 * which is exactly the sidebar's sort key, so it is the right signal to order by.
 *
 * `onUnknownConversation` is the other half: a frame naming a row this tab has never seen is a
 * conversation started somewhere else, and no local re-stamp can conjure it — only a refetch can. It is
 * called only in that case, so the frames a running turn republishes (which are all about rows already in
 * the list) cost nothing. Both phones refetch on every CONVERSATION frame; this is the same behaviour with
 * the redundant reads dropped.
 *
 * Ordering only. Whether a TURN is running in the open conversation is `useRemoteTurn`, off the same
 * channel; the two read different fields of the same frame and are deliberately separate hooks.
 * Mirrors the native `handleDuplexerConversationTurn` bump (iOS + Android), which does both from one
 * entry point because the native store is one object.
 */
export function useConversationRecency(onUnknownConversation?: () => void): void {
  useDuplexerSubscription<ConversationPush>(
    IndividualsDuplexerChannel.CONVERSATION,
    (payload) => {
      const entity = payload.entity;
      const id = entity?.id ?? payload.entityId;
      if (id) scheduleChatsInvalidation();
      if (!id || !entity?.updatedDate) return;
      if (!bumpConversationTo(id, entity.updatedDate)) onUnknownConversation?.();
    },
    // `created` as well as `updated`: a conversation started on another device publishes a create, and
    // the row it announces is the one most likely to be the user's next destination.
    ['created', 'updated'],
  );
}
